// Source-level autolinker: rewrites a markdown body so that IOC and entity
// occurrences become markdown links to sentinel URL fragments. The
// `markdownComponents.a` override in `Message.tsx` recognises those
// sentinels and renders them as the same `<a>` elements that the old
// DOM-mutation effects used to produce.
//
// Doing this at the markdown source level (rather than mutating the
// rendered DOM) keeps highlighting inside React's render tree, which
// eliminates the "Node.insertBefore: Child to insert before is not a
// child of this node" crash that the imperative effects caused.

import type { DetectedIOC, EntityLink } from '../../types';

// Dash variants normalised to ASCII '-'. All are single BMP code points
// so the normalised string has the same length as the original — indices
// are preserved 1:1.
const DASH_RE = /[‐‑‒–—−]/g;
const normalizeDashes = (s: string) => s.replace(DASH_RE, '-');

// Escape markdown-link-text metacharacters in matched text.
const escapeMdLinkText = (s: string) => s.replace(/[\\\[\]]/g, (m) => `\\${m}`);

const isWordChar = (c: string | undefined) => !!c && /\w/.test(c);

const ENTITY_DETAIL_LABELS = new Set([
  'ThreatActor',
  'MalwareFamily',
  'AttackTechnique',
]);

// Overloaded common-English aliases that the corpus index ships but that
// produce confusing false positives when they appear in prose unrelated
// to threat intel. Extend after observing real chats.
const ENTITY_STOPLIST: ReadonlySet<string> = new Set([
  'wave',
  'dust',
  'joker',
  'empire',
  'shadow',
  'snake',
  'cloud',
  'panda',
  'bear',
  'tiger',
  'spider',
  'dragon',
  'kitten',
]);

// Tokenize the source into spans we MUST emit verbatim and spans we may
// rewrite. Both passes share the same span definitions, except that
// inline code is verbatim for the entity pass but rewritable for the IOC
// pass (mirroring the old SKIP_TAGS = {A, PRE} vs {CODE, A, PRE} split).
interface Span {
  text: string;
  // 'plain' = rewritable; 'verbatim' = pass-through; 'code-inline' = pass
  // through for entity pass, rewritable for IOC pass.
  kind: 'plain' | 'verbatim' | 'code-inline';
}

function tokenize(src: string): Span[] {
  const spans: Span[] = [];
  let i = 0;
  let plainStart = 0;

  const flushPlain = (end: number) => {
    if (end > plainStart) {
      spans.push({ text: src.slice(plainStart, end), kind: 'plain' });
    }
  };

  while (i < src.length) {
    // Fenced code block — line-anchored opener (` ``` ` or ` ~~~ `, ≥3),
    // closer = same fence char and ≥ opener length, also line-anchored.
    if (i === 0 || src[i - 1] === '\n') {
      const fenceMatch = /^([`~]{3,})[^\n]*\n/.exec(src.slice(i));
      if (fenceMatch) {
        const fenceChar = fenceMatch[1][0];
        const fenceLen = fenceMatch[1].length;
        // Find closer
        const closerRe = new RegExp(
          `(^|\\n)(${fenceChar === '`' ? '`' : '~'}{${fenceLen},})[ \\t]*(\\n|$)`,
        );
        const rest = src.slice(i + fenceMatch[0].length);
        const closer = closerRe.exec(rest);
        const blockEnd =
          closer != null
            ? i + fenceMatch[0].length + closer.index + closer[0].length
            : src.length;
        flushPlain(i);
        spans.push({ text: src.slice(i, blockEnd), kind: 'verbatim' });
        i = blockEnd;
        plainStart = i;
        continue;
      }
    }

    const ch = src[i];

    // Backslash-escaped character — emit verbatim along with the next char.
    if (ch === '\\' && i + 1 < src.length) {
      flushPlain(i);
      spans.push({ text: src.slice(i, i + 2), kind: 'verbatim' });
      i += 2;
      plainStart = i;
      continue;
    }

    // Inline code span — backtick run of length n, terminator = same length.
    if (ch === '`') {
      let runLen = 1;
      while (src[i + runLen] === '`') runLen++;
      // Find closing run of the same length (and not part of a longer run).
      let j = i + runLen;
      let closeAt = -1;
      while (j < src.length) {
        if (src[j] === '`') {
          let k = j;
          while (src[k] === '`') k++;
          const len = k - j;
          if (len === runLen) {
            closeAt = j;
            break;
          }
          j = k;
        } else {
          j++;
        }
      }
      if (closeAt !== -1) {
        const end = closeAt + runLen;
        flushPlain(i);
        spans.push({ text: src.slice(i, end), kind: 'code-inline' });
        i = end;
        plainStart = i;
        continue;
      }
      // No closer — treat as plain text; fall through.
    }

    // Image or inline link: `![alt](url)` or `[text](url)`.
    if (ch === '[' || (ch === '!' && src[i + 1] === '[')) {
      const linkStart = ch === '!' ? i + 1 : i;
      // Find matching `]` allowing nested brackets inside text? Markdown
      // forbids unescaped `]` in link text — we accept up to first `]`.
      let k = linkStart + 1;
      let depth = 1;
      while (k < src.length && depth > 0) {
        const c = src[k];
        if (c === '\\' && k + 1 < src.length) {
          k += 2;
          continue;
        }
        if (c === '[') depth++;
        else if (c === ']') depth--;
        if (depth === 0) break;
        k++;
      }
      if (depth === 0 && k < src.length) {
        // After the `]`, look for `(...)` (inline link), `[...]` (ref),
        // or nothing (shortcut ref) — all should be emitted verbatim
        // because they are link constructs and we must not double-link.
        const afterBracket = k + 1;
        const next = src[afterBracket];
        if (next === '(') {
          // Scan for matching `)`, accepting escaped chars and balanced parens.
          let p = afterBracket + 1;
          let pdepth = 1;
          while (p < src.length && pdepth > 0) {
            const c = src[p];
            if (c === '\\' && p + 1 < src.length) {
              p += 2;
              continue;
            }
            if (c === '(') pdepth++;
            else if (c === ')') pdepth--;
            if (pdepth === 0) break;
            p++;
          }
          if (pdepth === 0 && p < src.length) {
            flushPlain(i);
            spans.push({ text: src.slice(i, p + 1), kind: 'verbatim' });
            i = p + 1;
            plainStart = i;
            continue;
          }
        } else if (next === '[') {
          // Reference-style `[text][label]`.
          let p = afterBracket + 1;
          while (p < src.length && src[p] !== ']') {
            if (src[p] === '\\' && p + 1 < src.length) p += 2;
            else p++;
          }
          if (p < src.length) {
            flushPlain(i);
            spans.push({ text: src.slice(i, p + 1), kind: 'verbatim' });
            i = p + 1;
            plainStart = i;
            continue;
          }
        }
        // Shortcut reference `[text]` (no following `(...)`/`[...]`).
        // We err on the safe side and emit verbatim — there is no useful
        // entity-link rewrite to perform inside an unknown link construct.
        flushPlain(i);
        spans.push({ text: src.slice(i, k + 1), kind: 'verbatim' });
        i = k + 1;
        plainStart = i;
        continue;
      }
      // No closing `]` — fall through and treat the `[` as plain.
    }

    // Autolink `<scheme://...>` or `<email@...>`.
    if (ch === '<') {
      const am = /^<([a-zA-Z][a-zA-Z0-9+.\-]*:[^\s>]+|[^\s@>]+@[^\s>]+)>/.exec(
        src.slice(i),
      );
      if (am) {
        flushPlain(i);
        spans.push({ text: am[0], kind: 'verbatim' });
        i += am[0].length;
        plainStart = i;
        continue;
      }
    }

    // Bare URL: http(s)://… or www.…  Match a non-whitespace run; trim
    // common trailing punctuation that should not be part of the URL.
    if (
      (ch === 'h' &&
        (src.startsWith('http://', i) || src.startsWith('https://', i))) ||
      (ch === 'w' && src.startsWith('www.', i))
    ) {
      let end = i;
      while (end < src.length && !/\s/.test(src[end])) end++;
      // Trim trailing punctuation that's almost certainly not in the URL.
      while (end > i && /[.,;:!?)\]}>'"]/.test(src[end - 1])) end--;
      if (end > i) {
        flushPlain(i);
        spans.push({ text: src.slice(i, end), kind: 'verbatim' });
        i = end;
        plainStart = i;
        continue;
      }
    }

    i++;
  }

  flushPlain(src.length);
  return spans;
}

// IOC rewrite over a plain text segment. Returns the (possibly rewritten)
// markdown source for the segment.
function rewriteIocs(
  segment: string,
  iocMap: Map<string, DetectedIOC>,
  rawValues: string[],
  maxLen: number,
): string {
  if (rawValues.length === 0) return segment;
  const normalized = normalizeDashes(segment);
  let out = '';
  let i = 0;
  while (i < segment.length) {
    let matched: string | null = null;
    // Bound scan to remaining length and longest raw value to short-circuit.
    const remaining = segment.length - i;
    const cap = Math.min(maxLen, remaining);
    for (const raw of rawValues) {
      if (raw.length > cap) continue;
      if (normalized.startsWith(raw, i)) {
        matched = raw;
        break; // rawValues is longest-first → first hit wins
      }
    }
    if (matched) {
      const ioc = iocMap.get(matched)!;
      const display = segment.slice(i, i + matched.length);
      const href =
        `#mp-ioc:${encodeURIComponent(ioc.type)}:${encodeURIComponent(ioc.value)}`;
      out += `[${escapeMdLinkText(display)}](${href})`;
      i += matched.length;
    } else {
      out += segment[i];
      i++;
    }
  }
  return out;
}

// Entity rewrite over a plain text segment. Whole-word match, case-insensitive,
// longest-first. Runs over the IOC pass's output so freshly inserted IOC
// links are present in the tokenizer as `[…](…)` inline links and will be
// skipped naturally.
function rewriteEntities(
  segment: string,
  linkMap: Map<string, EntityLink>,
  names: string[],
  maxLen: number,
): string {
  if (names.length === 0) return segment;
  const lower = segment.toLowerCase();
  let out = '';
  let i = 0;
  while (i < segment.length) {
    let matched: string | null = null;
    const remaining = segment.length - i;
    const cap = Math.min(maxLen, remaining);
    for (const name of names) {
      if (name.length > cap) continue;
      if (!lower.startsWith(name, i)) continue;
      const before = i > 0 ? segment[i - 1] : '';
      const after = i + name.length < segment.length ? segment[i + name.length] : '';
      if (isWordChar(before) || isWordChar(after)) continue;
      matched = name;
      break;
    }
    if (matched) {
      const link = linkMap.get(matched)!;
      const display = segment.slice(i, i + matched.length);
      const isDetail = ENTITY_DETAIL_LABELS.has(link.label);
      const sentinel = isDetail ? '#mp-entity' : '#mp-graph';
      const href = `${sentinel}:${encodeURIComponent(link.label)}:${encodeURIComponent(link.name)}`;
      out += `[${escapeMdLinkText(display)}](${href})`;
      i += matched.length;
    } else {
      out += segment[i];
      i++;
    }
  }
  return out;
}

export function autolinkMarkdown(
  src: string,
  iocs: DetectedIOC[] | undefined,
  entities: EntityLink[] | undefined,
): string {
  if (!src) return '';
  const hasIocs = iocs && iocs.length > 0;
  const hasEntities = entities && entities.length > 0;
  if (!hasIocs && !hasEntities) return src;

  // Build IOC lookup (first-wins by raw_value, longest-first).
  const iocMap = new Map<string, DetectedIOC>();
  if (hasIocs) {
    for (const ioc of iocs!) {
      if (!iocMap.has(ioc.raw_value)) iocMap.set(ioc.raw_value, ioc);
    }
  }
  const rawValues = [...iocMap.keys()].sort((a, b) => b.length - a.length);
  const iocMaxLen = rawValues.length ? rawValues[0].length : 0;

  // Build entity lookup (first-wins by lowercased name, longest-first).
  // Both canonical names and aliases land in the same map; the link's
  // `name` field controls the href so e.g. "Fancy Bear" routes to APT28.
  const linkMap = new Map<string, EntityLink>();
  if (hasEntities) {
    const register = (key: string, link: EntityLink) => {
      const k = key.toLowerCase();
      if (!k || ENTITY_STOPLIST.has(k)) return;
      if (!linkMap.has(k)) linkMap.set(k, link);
    };
    for (const link of entities!) {
      register(link.name, link);
      if (link.aliases) {
        for (const alias of link.aliases) register(alias, link);
      }
    }
  }
  const names = [...linkMap.keys()].sort((a, b) => b.length - a.length);
  const entMaxLen = names.length ? names[0].length : 0;

  // Pass 1: IOC. Tokenize the original source, rewrite IOCs in plain and
  // inline-code segments (matches the old behaviour where IOCs were
  // highlighted inside <code> but not entity-linked).
  const spans1 = tokenize(src);
  const afterIocs = spans1
    .map((s) => {
      if (s.kind === 'verbatim') return s.text;
      // plain and code-inline both get IOC rewrite
      if (s.kind === 'code-inline') {
        // Rewrite IOCs *inside* the code span text, preserving the backticks.
        // The span starts with a run of backticks and ends with the same run.
        const openLen = /^`+/.exec(s.text)![0].length;
        const open = s.text.slice(0, openLen);
        const close = s.text.slice(s.text.length - openLen);
        const inner = s.text.slice(openLen, s.text.length - openLen);
        return open + rewriteIocs(inner, iocMap, rawValues, iocMaxLen) + close;
      }
      return rewriteIocs(s.text, iocMap, rawValues, iocMaxLen);
    })
    .join('');

  if (!hasEntities) return afterIocs;

  // Pass 2: entities. Re-tokenize the IOC-rewritten source so the new
  // `[text](#mp-ioc:…)` links are recognised as inline links and skipped.
  // Entity rewriting runs only on plain (not code-inline) segments, matching
  // the old SKIP_TAGS = {CODE, A, PRE} behaviour.
  const spans2 = tokenize(afterIocs);
  return spans2
    .map((s) => {
      if (s.kind === 'plain') {
        return rewriteEntities(s.text, linkMap, names, entMaxLen);
      }
      return s.text;
    })
    .join('');
}
